/**
 * WEB-1016 — контрастные варианты семантических акцентов.
 *
 * Двухцветную семантику (деньги / премиум) завёл WEB-925 атом 2: пак не
 * задаёт своих цветов, он лишь называет ИСТОЧНИК из палитры (`success`,
 * `accent`, `brand`). Дыра была в том, что источник брался СЫРЫМ, а палитра
 * у воронки произвольная: `accent` в контракте палитры помечен «reserved», и
 * у части тем это светлый тинт (#f5f5f5, #ffeedd). Такой «акцент» на
 * светлой странице не видно вовсе (замер: 1.02–1.31:1 при норме 3:1), а
 * заливка чипа «Most Popular» им же с белым текстом давала белое по белому.
 *
 * Лечение — то же, которым WEB-916 чинил ссылки и звёзды рейтинга: цвет
 * доводится до порога контраста с поверхностями, на которых он реально
 * рисуется, оттенок при этом сохраняется. Новых цветов здесь не появляется:
 * это по-прежнему цвет палитры, только доведённый до видимого.
 */
import { contrastRatio, darkenToContrastAll, pickOnPrimaryColor } from './colorUtils';

/**
 * Нетекстовый элемент (WCAG 1.4.11): рамка выделенного тарифа, чип, обводка
 * промо-тикета, ряд платёжных плашек.
 */
export const ACCENT_CONTRAST_TARGET = 3;

/** Текст на заливке акцента (WCAG 1.4.3). */
export const ON_ACCENT_CONTRAST_TARGET = 4.5;

/**
 * Цвет акцента, доведённый до различимости на своих подложках.
 * Непарсимый вход `darkenToContrastAll` возвращает как есть — вёрстка не
 * должна падать из-за мусора в палитре.
 */
export function semanticAccentInk(color: string, surfaces: readonly string[]): string {
  return darkenToContrastAll(color, surfaces, ACCENT_CONTRAST_TARGET);
}

/**
 * Цвет текста ПОВЕРХ заливки акцентом.
 *
 * Не «притемнить от кандидата», а ВЫБРАТЬ сторону по замеру. Дожимать шагами
 * тут нельзя: направление шага задаётся светлотой подложки, и для заливки
 * средней светлоты кандидат уезжает в сторону ХУДШЕГО контраста (замер на
 * soft-cream/warm-beige/calm-teal: 3.94–4.32 вместо 4.5).
 *
 * Почему выбор всегда достаточен: чёрное и белое сходятся в худшей точке при
 * светлоте заливки ≈0.179, и там оба дают 4.58:1 — то есть лучший из двух
 * НИКОГДА не опускается ниже порога. Мягкий тёмный `#1a1a1a` (общая
 * договорённость проекта, `pickOnPrimaryColor`) предпочитается, пока он сам
 * держит порог, — чтобы подписи не становились резко-чёрными без нужды.
 */
export function onSemanticAccent(ink: string): string {
  const soft = pickOnPrimaryColor(ink);
  if (contrastRatio(soft, ink) >= ON_ACCENT_CONTRAST_TARGET) return soft;
  const black = '#000000';
  const white = '#ffffff';
  return contrastRatio(black, ink) >= contrastRatio(white, ink) ? black : white;
}

/** Роль акцента → пара переменных «цвет» / «текст на нём». */
export const ACCENT_INK_VARS = {
  brand: '--color-brand-ink',
  success: '--color-success-ink',
  accent: '--color-accent-ink',
} as const;

export const ACCENT_ON_VARS = {
  brand: '--color-on-brand-ink',
  success: '--color-on-success-ink',
  accent: '--color-on-accent-ink',
} as const;

export type AccentInkRole = keyof typeof ACCENT_INK_VARS;

/**
 * Эмит обеих переменных для одной роли. Зовут и `themeToStyle`, и
 * `paletteToStyle` — иначе роль, посчитанная в одной ветке и забытая в
 * другой, тихо разъедется (шрам WEB-916: порог держался против одной
 * поверхности из двух).
 */
export function emitAccentInk(
  style: Record<string, string>,
  role: AccentInkRole,
  base: string,
  surfaces: readonly string[],
): void {
  const ink = semanticAccentInk(base, surfaces);
  style[ACCENT_INK_VARS[role]] = ink;
  style[ACCENT_ON_VARS[role]] = onSemanticAccent(ink);
}
